/**
 * Derived report metrics.
 *
 * Everything here is computed from the existing `report` query payload — no new
 * API surface. Keeping the derivation in one place means the tabs stay
 * presentational and the same number is never computed two different ways.
 */

export interface SeriesPoint {
	date: string;
	value: number;
	label: string;
}

export interface ReportModel {
	totalSales: number;
	totalServiceSales: number;
	totalProductSales: number;
	totalCustomers: number;
	totalNewCustomers: number;
	totalExistingCustomers: number;
	orders: any[];
	paymentReport: { cash: number; card: number; upi: number };
	dailySales: SeriesPoint[];
	weeklySales: SeriesPoint[];
	monthlySales: SeriesPoint[];
	comparisonMetrics?: { monthOverMonth?: number; previousPeriodSales?: number };
	staffPerformance: any[];
	serviceCategories: any[];
	productCategories: any[];
	inventoryAnalysis: any[];
	lowStockCount: number;
	overstockCount: number;
}

export const emptyReport: ReportModel = {
	totalSales: 0,
	totalServiceSales: 0,
	totalProductSales: 0,
	totalCustomers: 0,
	totalNewCustomers: 0,
	totalExistingCustomers: 0,
	orders: [],
	paymentReport: { cash: 0, card: 0, upi: 0 },
	dailySales: [],
	weeklySales: [],
	monthlySales: [],
	comparisonMetrics: undefined,
	staffPerformance: [],
	serviceCategories: [],
	productCategories: [],
	inventoryAnalysis: [],
	lowStockCount: 0,
	overstockCount: 0,
};

export const toReportModel = (raw: any): ReportModel => ({
	...emptyReport,
	...(raw ?? {}),
	orders: raw?.orders ?? [],
	paymentReport: raw?.paymentReport ?? emptyReport.paymentReport,
	dailySales: raw?.dailySales ?? [],
	weeklySales: raw?.weeklySales ?? [],
	monthlySales: raw?.monthlySales ?? [],
	staffPerformance: raw?.staffPerformance ?? [],
	serviceCategories: raw?.serviceCategories ?? [],
	productCategories: raw?.productCategories ?? [],
	inventoryAnalysis: raw?.inventoryAnalysis ?? [],
});

/** Percentage change from `previous` to `current`, or undefined when there is no base. */
export const delta = (current: number, previous?: number): number | undefined => {
	if (previous === undefined || previous === null || previous === 0) return undefined;
	return ((current - previous) / Math.abs(previous)) * 100;
};

export const orderCount = (report: ReportModel): number => report.orders.length;

export const averageOrderValue = (report: ReportModel): number =>
	report.orders.length ? report.totalSales / report.orders.length : 0;

/** Total discount given away across services and products in the period. */
export const totalDiscount = (report: ReportModel): number =>
	report.orders.reduce((sum: number, order: any) => {
		const services = (order.services ?? []).reduce(
			(s: number, item: any) => s + (item.discountAmount ?? 0),
			0
		);
		const products = (order.products ?? []).reduce(
			(s: number, item: any) => s + (item.discountAmount ?? 0),
			0
		);
		return sum + services + products;
	}, 0);

export const repeatRate = (report: ReportModel): number =>
	report.totalCustomers ? (report.totalExistingCustomers / report.totalCustomers) * 100 : 0;

export interface LineItemRow {
	name: string;
	revenue: number;
	quantity: number;
	averagePrice: number;
	share: number;
}

/** Ranked categories with share of total, for either services or products. */
export const rankedCategories = (categories: any[]): LineItemRow[] => {
	const total = categories.reduce((sum, category) => sum + (category.revenue ?? 0), 0);
	return categories
		.map((category) => ({
			name: category.categoryName ?? 'Uncategorised',
			revenue: category.revenue ?? 0,
			quantity: category.count ?? 0,
			averagePrice: category.count ? (category.revenue ?? 0) / category.count : 0,
			share: category.percentage ?? (total ? ((category.revenue ?? 0) / total) * 100 : 0),
		}))
		.sort((a, b) => b.revenue - a.revenue);
};

/** Revenue by hour of day (0–23) — shows when the chairs are actually full. */
export const revenueByHour = (report: ReportModel): { hour: number; revenue: number; orders: number }[] => {
	const buckets = Array.from({ length: 24 }, (_, hour) => ({ hour, revenue: 0, orders: 0 }));
	report.orders.forEach((order: any) => {
		if (!order.createdAt) return;
		const hour = new Date(order.createdAt).getHours();
		if (Number.isNaN(hour)) return;
		buckets[hour].revenue += order.total ?? 0;
		buckets[hour].orders += 1;
	});
	return buckets;
};

/** Ticket-size distribution — where the money actually comes from. */
export const orderValueBuckets = (
	report: ReportModel
): { label: string; count: number; revenue: number }[] => {
	const edges = [0, 200, 500, 1000, 2000, Infinity];
	const labels = ['< ₹200', '₹200–500', '₹500–1K', '₹1K–2K', '₹2K+'];
	const buckets = labels.map((label) => ({ label, count: 0, revenue: 0 }));
	report.orders.forEach((order: any) => {
		const total = order.total ?? 0;
		const index = edges.findIndex((edge, i) => total >= edge && total < edges[i + 1]);
		if (index >= 0) {
			buckets[index].count += 1;
			buckets[index].revenue += total;
		}
	});
	return buckets;
};

export interface CustomerRow {
	name: string;
	orders: number;
	spend: number;
}

/** Top customers by spend in the period, from the order list. */
export const topCustomers = (report: ReportModel, limit = 8): CustomerRow[] => {
	const byName = new Map<string, CustomerRow>();
	report.orders.forEach((order: any) => {
		const name = (order.customerName ?? '').trim();
		if (!name) return;
		const current = byName.get(name) ?? { name, orders: 0, spend: 0 };
		current.orders += 1;
		current.spend += order.total ?? 0;
		byName.set(name, current);
	});
	return Array.from(byName.values())
		.sort((a, b) => b.spend - a.spend)
		.slice(0, limit);
};

/** Trend granularity that actually has enough points to be worth drawing. */
export const bestGranularity = (report: ReportModel): 'daily' | 'weekly' | 'monthly' => {
	if (report.dailySales.length > 1 && report.dailySales.length <= 62) return 'daily';
	if (report.weeklySales.length > 1) return 'weekly';
	if (report.monthlySales.length > 1) return 'monthly';
	return 'daily';
};

/**
 * Collapse a long daily series so the x-axis stays readable. Under the
 * threshold the series is returned untouched.
 */
export const condense = (points: SeriesPoint[], maxPoints = 45): SeriesPoint[] => {
	if (points.length <= maxPoints) return points;
	const groupSize = Math.ceil(points.length / maxPoints);
	const grouped: SeriesPoint[] = [];
	for (let i = 0; i < points.length; i += groupSize) {
		const group = points.slice(i, i + groupSize);
		const first = group[0];
		const last = group[group.length - 1];
		grouped.push({
			date: first.date,
			value: Math.round(group.reduce((sum, point) => sum + point.value, 0) * 100) / 100,
			label: group.length > 1 ? `${first.label} – ${last.label}` : first.label,
		});
	}
	return grouped;
};
